import React from 'react';
import { Grid } from '@mui/material';
import {
  LocalDrink as LocalDrinkIcon,
  NaturePeople as NaturePeopleIcon,
  Restaurant as RestaurantIcon,
  QueryBuilder as QueryBuilderIcon,
  DirectionsCar as DirectionsCarIcon,
} from '@mui/icons-material';
import EnviroImpactCard from '../../../../../components/EnviroImpactCard';
import { ReactComponent as FoodIcon } from '../../../../../assets/svg/food.svg';
import { Colors } from '../../../../../assets/theme/Colors';
import {
  formatNumber,
  formatEnvironmentImpactDistance,
  formatEnvironmentImpactDuration,
} from '../../../../../helpers/formatters';

const iconSx = {
  color: Colors.appBar.main,
};
const foodSx = {
  fill: Colors.appBar.main,
};
const cardSx = {
  height: '100%',
};

const MyEnviroImpact = ({
  isLoading,
  environmentImpact,
  iconSize = 72,
  valueFontVariant = 'h5',
  textStyle = {},
  textVariant = 'body2',
  isRescuer = false,
  // hours and miles are the rescuers' driving, not something food donors did
  showRescuerEffort = true,
}) => {
  const environmentImpactDuration = formatEnvironmentImpactDuration(
    environmentImpact && environmentImpact.duration ? environmentImpact.duration : 0
  );
  const environmentImpactDistance = formatEnvironmentImpactDistance(
    environmentImpact && environmentImpact.distance ? environmentImpact.distance : 0
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <EnviroImpactCard
          value={environmentImpact && environmentImpact.weight_lbs ? formatNumber(environmentImpact.weight_lbs) : 0}
          title={isRescuer ? "You've rescued" : 'Lbs of food rescued'}
          subTitle={isRescuer ? 'pounds of food!' : ''}
          isLoading={isLoading}
          cardStyle={cardSx}
          iconSize={iconSize}
          valueFontVariant={valueFontVariant}
          textStyle={textStyle}
          textVariant={textVariant}
        >
          {/* Sets size to the same values as icons from mui */}
          <FoodIcon viewBox="0 0 48 48" width={iconSize} height={iconSize} style={foodSx} />
        </EnviroImpactCard>
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <EnviroImpactCard
          value={environmentImpact && environmentImpact.meals ? formatNumber(environmentImpact.meals) : 0}
          title={isRescuer ? "You've delivered" : 'Meals rescued'}
          subTitle={isRescuer ? 'meals to your community!' : ''}
          isLoading={isLoading}
          cardStyle={cardSx}
          iconSize={iconSize}
          valueFontVariant={valueFontVariant}
          textStyle={textStyle}
          textVariant={textVariant}
        >
          <RestaurantIcon sx={iconSx} fontSize="inherit" />
        </EnviroImpactCard>
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <EnviroImpactCard
          value={environmentImpact && environmentImpact.co2eq ? formatNumber(Math.abs(environmentImpact.co2eq)) : 0}
          title={isRescuer ? "You've offset" : 'Lbs of CO2eq offset'}
          subTitle={
            isRescuer ? (
              <span>
                pounds of C0<sub>2</sub>eq emissions!
              </span>
            ) : (
              ''
            )
          }
          isLoading={isLoading}
          cardStyle={cardSx}
          iconSize={iconSize}
          valueFontVariant={valueFontVariant}
          textStyle={textStyle}
          textVariant={textVariant}
        >
          <NaturePeopleIcon sx={iconSx} fontSize="inherit" />
        </EnviroImpactCard>
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <EnviroImpactCard
          value={environmentImpact && environmentImpact.water ? formatNumber(Math.abs(environmentImpact.water)) : 0}
          title={isRescuer ? "You've prevented" : 'Gallons of water wasted offset'}
          subTitle={isRescuer ? 'gallons of water from being wasted!' : ''}
          isLoading={isLoading}
          cardStyle={cardSx}
          iconSize={iconSize}
          valueFontVariant={valueFontVariant}
          textStyle={textStyle}
          textVariant={textVariant}
        >
          <LocalDrinkIcon sx={iconSx} fontSize="inherit" />
        </EnviroImpactCard>
      </Grid>

      {showRescuerEffort && (
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <EnviroImpactCard
            value={formatNumber(environmentImpactDuration.value)}
            title={isRescuer ? "You've spent" : 'Hours spent'}
            subTitle={isRescuer ? `${environmentImpactDuration.metric} helping your community!` : ''}
            isLoading={isLoading}
            cardStyle={cardSx}
            iconSize={iconSize}
            valueFontVariant={valueFontVariant}
            textStyle={textStyle}
            textVariant={textVariant}
            showTooltip
            tooltipText="Hours are calculated as the time spent driving from the pickup location and dropoff location"
          >
            <QueryBuilderIcon sx={iconSx} fontSize="inherit" />
          </EnviroImpactCard>
        </Grid>
      )}

      {showRescuerEffort && (
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <EnviroImpactCard
            value={formatNumber(environmentImpactDistance.value)}
            title={isRescuer ? "You've driven" : 'Miles driven'}
            subTitle={isRescuer ? `${environmentImpactDistance.metric} bringing surplus food to people in need!` : ''}
            isLoading={isLoading}
            cardStyle={cardSx}
            iconSize={iconSize}
            valueFontVariant={valueFontVariant}
            textStyle={textStyle}
            textVariant={textVariant}
            showTooltip
            tooltipText="Mileage is calculated as the distance between the pickup and dropoff locations"
          >
            <DirectionsCarIcon sx={iconSx} fontSize="inherit" />
          </EnviroImpactCard>
        </Grid>
      )}
    </Grid>
  );
};
export default MyEnviroImpact;
